import { gsap } from "gsap";
import { ISequencePlay, Sequence } from "../../../../core/displayobject/Sequence";
import { Helper } from "../../../../core/utils/Helper";
import { SpinePlayConfig, SpinePlayer } from "../../../../core/displayobject/SpinePlayer";
import { PREngineManager } from "../../../punkEngine/PREngineManager";
import { PunkRocker } from "../../../PunkRocker";
import { BLEND_MODES } from "pixi.js";
import {LayoutDimData} from "../../../../core/utils/ResizeHelper";
import {GameConfig} from "../../../config/base/GameConfig";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {ReelEngineManager} from "../../../../core/reel/ReelEngineManager";
import {GameSetting} from "../../../../core/setting/GameSetting";

export interface OverlayItem {
    item: PIXI.DisplayObject;
    timeline:gsap.core.Timeline;
}

export class OverlayAnimation extends PIXI.Container {

    private bottomTrainVisible: boolean = false;

    constructor() {
        super();
        this.name = "OverlayAnimation";

        this.onResize();
    }

    private createGraphic(sizeX:number = 100,sizeY?:number,color?:number): PIXI.Graphics {
        if(!sizeY) sizeY = sizeX;
        return this.addChild(Helper.createBasicGraphic(
            color != undefined ? color : 0xFFFFFF,1,
            -sizeX*0.5, -sizeY*0.5,
            sizeX, sizeY));
    }

    public playStandAloneSequence(texture:PIXI.Texture[], conf:ISequencePlay = {},layer?:PIXI.Container):OverlayItem{
        const item:Sequence = layer ? layer.addChild(Sequence.createSequence(texture)) : this.addChild(Sequence.createSequence(texture));

        const tl:gsap.core.Timeline = Helper.temporaryItemTl(item);
        
        tl.add(item.play(conf));
        
        return {item:item,timeline:tl};
    }

    public addChild<TChildren extends PIXI.DisplayObject[]>(...child: TChildren): TChildren[0]{
        super.addChild(...child);
        this.sortChildren();
        return child[0];
    }

    public playStandAloneSpine(path:string, conf:SpinePlayConfig,layer?:PIXI.Container ):OverlayItem{
        const item:SpinePlayer = layer ? layer.addChild(new SpinePlayer(path)) : this.addChild(new SpinePlayer(path))
        const tl:gsap.core.Timeline = Helper.temporaryItemTl(item);
        
        tl.add(item.play(conf));

        return {item:item,timeline:tl};
    }

    public playSpine(target: PIXI.DisplayObject, path: string, conf: SpinePlayConfig, stay: boolean = false, cont?: PIXI.Container, initVisible:boolean = true): OverlayItem {
        const item: SpinePlayer = cont ? cont.addChild(new SpinePlayer(path, initVisible)) : this.addChild(new SpinePlayer(path, initVisible))
        const tl: gsap.core.Timeline = stay ? new gsap.core.Timeline({
            onUpdate: () => {
                if (target) {
                    item.position = Helper.copyLocalPosition(item, target)
                } else {
                    tl.kill();
                    item.destroy();
                }
                conf.onUpdate ? conf.onUpdate() : undefined;
            }
        }) : this.createItemTlToFollow(target, item);

        item.position = Helper.copyLocalPosition(item, target)
        tl.add(item.play(conf));

        return { item: item, timeline: tl };
    }
  
    public playSequence(target:PIXI.DisplayObject, texture:PIXI.Texture[], conf:ISequencePlay = {},layer?:PIXI.Container):OverlayItem{
        //#125 feedbacks
        let item: Sequence;
        if(conf.onIndex === undefined) {
            item = layer ? layer.addChild(Sequence.createSequence(texture)) : this.addChild(Sequence.createSequence(texture));
        }else{
            item = layer ? layer.addChildAt(Sequence.createSequence(texture), conf.onIndex) : this.addChildAt(Sequence.createSequence(texture), conf.onIndex);
        }

        // #310 - hiding wildFire
        if(ReelEngineManager.isQuickStop || GameSetting.isFastSpin) {
            item.timeScale = 3;
        }

        const tl:gsap.core.Timeline = this.createItemTlToFollow(target,item);

        tl.add(item.play(conf));
        
        return {item:item,timeline:tl};
    }

    public flashGFX(target:PIXI.DisplayObject,sizeX?:number,sizeY?:number, durr:number = 0.5, color?:number):OverlayItem{

        const item:PIXI.DisplayObject = this.createGraphic(sizeX, sizeY,color);

        const tl:gsap.core.Timeline = this.createItemTlToFollow(target,item);

        tl.fromTo(item,{alpha:0},{alpha:1, duration:durr/2});
        tl.to(item,{alpha:0, duration:durr/2});
        return {item:item,timeline:tl};
    }

    public useItemAsOverlay(target: PIXI.DisplayObject, item: PIXI.DisplayObject): OverlayItem {
        const tl: gsap.core.Timeline = this.createItemTlToFollow(target, item);
        this.addChild(item);
        return { item: item, timeline: tl };
    }

    private createItemTlToFollow(target:PIXI.DisplayObject, item:PIXI.DisplayObject):gsap.core.Timeline{
        const tl = new gsap.core.Timeline({
            onUpdate:() =>{
                if(!target || !target.parent){
                    remove();
                }else if(item.transform){
                    item.position = Helper.copyLocalPosition(item,target)
                }
            },
            onComplete:() => remove()
        });

        item.position = Helper.copyLocalPosition(item,target)

        function remove(){
            tl.kill();
            item.transform && item.destroy();
            target = undefined!;
        }

        return tl;
    }

    public removeNonUsedAnimations():void{
        this.children.forEach(s => {if(s.name != "mask") s.destroy()});
    }

    public flashMystery(reel:number,row:number){
        const toPosition: PIXI.Point = PunkRocker.reelEngineManager.getSymPos(reel,row);
        
        let sp:Sequence = this.addChild(new Sequence({texturePath:"M/4/M_normal"}));
        sp.name = "xWaysFlash";
        sp.blendMode = BLEND_MODES.ADD;
        sp.position = toPosition;
        sp.anchor.set(0.5,0.5);
        
        gsap.fromTo(sp,{alpha:1},{alpha:0,duration:2}).eventCallback("onComplete", () =>{
            sp.destroy();
        })
    }

    public onResize():void{
        const scaledReelDim: LayoutDimData = this.bottomTrainVisible ? PREngineManager.dimScaled: PREngineManager.dim;
        this.scale.set(scaledReelDim.scale.x, scaledReelDim.scale.y);

        for (let i: number = 0; i < this.children.length; i++) {
            const currChild: any = this.children[i];

            if(currChild.name === "xWaysFlash") {
                if(NolimitApplication.isLandscape) {
                    currChild.position.x += 30;
                }else{
                    currChild.position.x -= 30;
                }
            }
        }
    }

    public onBottomTrainShow(flag: boolean, isRestore: boolean = false): void {
        this.bottomTrainVisible = flag;
        const scaledReelDim: LayoutDimData = flag ? PREngineManager.dimScaled : PREngineManager.dim;

        if (flag) {
            gsap.to(this.scale, {
                x: scaledReelDim.scale.x,
                y: scaledReelDim.scale.y,
                duration: isRestore ? 0.001 : GameConfig.FRAME_EXPAND_DUR
            });
        } else {
            this.scale.set(scaledReelDim.scale.x, scaledReelDim.scale.y);
        }
    }
}